import {Injectable} from '@angular/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';

import {Store} from '@stores/abstract-store';
import {ChatMessage} from '@models/chat-message-types';
import {ProtoCard} from '@models/proto-card-types';

export interface SessionState {
  chatSessionId: string;
  messages: ChatMessage[];
  protoCards: ProtoCard[];
  isLoaded: boolean;
  isStreaming: boolean;
  toolStatus: string | null;
  systemPrompt?: string;
  deviceName?: string;
  /** Projection of the server-side agent facts (the server owns the full state) */
  agentContext?: APIM.AgentContextProjection;
  /** Set when the server paused the turn and is waiting for a `resume` request */
  pendingResumeToolUseId?: string;
}

export interface AgentMemoryState {
  sessions: Record<string, SessionState>;
}

@Injectable({providedIn: 'root'})
export class AgentMemoryStore extends Store<AgentMemoryState> {
  constructor() {
    super({sessions: {}});
  }

  getSession(chatSessionId: string): SessionState | undefined {
    return this.state().sessions[chatSessionId];
  }

  isLoaded(chatSessionId: string): boolean {
    return this.state().sessions[chatSessionId]?.isLoaded ?? false;
  }

  initSession(chatSessionId: string) {
    const sessions = {
      ...this.state().sessions,
      [chatSessionId]: {
        chatSessionId,
        messages: [],
        protoCards: [],
        isLoaded: false,
        isStreaming: false,
        toolStatus: null,
      },
    };
    this.setState({sessions});
  }

  markLoaded(chatSessionId: string) {
    this._updateSession(chatSessionId, (s) => ({...s, isLoaded: true}));
  }

  addMessage(chatSessionId: string, message: ChatMessage) {
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      messages: [...s.messages, message],
    }));
  }

  updateStreamingMessage(
    chatSessionId: string,
    messageId: string,
    content: string,
  ) {
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      messages: s.messages.map((m) =>
        m.id === messageId ? {...m, content} : m,
      ),
    }));
  }

  completeStreamingMessage(chatSessionId: string, messageId: string) {
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      messages: s.messages.map((m) =>
        m.id === messageId ? {...m, isStreaming: false} : m,
      ),
    }));
  }

  /**
   * Replaces the card with the same cardId wherever it is shown (an updated card); returns
   * false when there is none.
   */
  replaceProtoCard(chatSessionId: string, card: ProtoCard): boolean {
    const session = this.getSession(chatSessionId);
    const shown = session?.messages.some((m) =>
      m.protoCards?.some((c) => c.cardId === card.cardId),
    );
    if (!shown) return false;
    const swap = (cards: ProtoCard[]) =>
      cards.map((c) => (c.cardId === card.cardId ? card : c));
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      protoCards: swap(s.protoCards),
      messages: s.messages.map((m) =>
        m.protoCards ? {...m, protoCards: swap(m.protoCards)} : m,
      ),
    }));
    return true;
  }

  /** Merges fields into the data of every visualization card for this version (its key). */
  updateVisualizationCards(
    chatSessionId: string,
    key: string,
    data: Record<string, unknown>,
  ) {
    const patch = (cards: ProtoCard[]) =>
      cards.map((c) =>
        c.type === 'visualization' && c.data['key'] === key
          ? {...c, data: {...c.data, ...data}}
          : c,
      );
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      protoCards: patch(s.protoCards),
      messages: s.messages.map((m) =>
        m.protoCards ? {...m, protoCards: patch(m.protoCards)} : m,
      ),
    }));
  }

  addProtoCard(chatSessionId: string, messageId: string, card: ProtoCard) {
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      protoCards: [...s.protoCards, card],
      messages: s.messages.map((m) =>
        m.id === messageId
          ? {...m, protoCards: [...(m.protoCards ?? []), card]}
          : m,
      ),
    }));
  }

  /** Removes the card from the session and from every message that shows it. */
  removeProtoCard(chatSessionId: string, cardId: string) {
    const keep = (cards: ProtoCard[]) =>
      cards.filter((c) => c.cardId !== cardId);
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      protoCards: keep(s.protoCards),
      messages: s.messages.map((m) =>
        m.protoCards ? {...m, protoCards: keep(m.protoCards)} : m,
      ),
    }));
  }

  setStreaming(chatSessionId: string, isStreaming: boolean) {
    this._updateSession(chatSessionId, (s) => ({...s, isStreaming}));
  }

  setToolStatus(chatSessionId: string, toolStatus: string | null) {
    this._updateSession(chatSessionId, (s) => ({...s, toolStatus}));
  }

  setSystemPrompt(chatSessionId: string, systemPrompt: string) {
    this._updateSession(chatSessionId, (s) => ({...s, systemPrompt}));
  }

  setDeviceName(chatSessionId: string, deviceName: string) {
    this._updateSession(chatSessionId, (s) => ({...s, deviceName}));
  }

  getDeviceName(chatSessionId: string): string | undefined {
    return this.state().sessions[chatSessionId]?.deviceName;
  }

  setAgentContext(
    chatSessionId: string,
    agentContext: APIM.AgentContextProjection,
  ) {
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      agentContext,
      deviceName: agentContext.deviceName ?? s.deviceName,
    }));
  }

  setPendingResume(chatSessionId: string, toolUseId: string | undefined) {
    this._updateSession(chatSessionId, (s) => ({
      ...s,
      pendingResumeToolUseId: toolUseId,
    }));
  }

  clearSession(chatSessionId: string) {
    const {[chatSessionId]: _, ...rest} = this.state().sessions;
    this.setState({sessions: rest});
  }

  private _updateSession(
    chatSessionId: string,
    updater: (s: SessionState) => SessionState,
  ) {
    const session = this.state().sessions[chatSessionId];
    if (!session) return;
    this.setState({
      sessions: {
        ...this.state().sessions,
        [chatSessionId]: updater(session),
      },
    });
  }
}
